import {AuthProfile, Wish} from "@/api/types.ts";
import {Badge, Caption, Cell, Image, Navigation, IconButton} from "@telegram-apps/telegram-ui";
import {TbGift, TbChevronUp, TbChevronDown} from "react-icons/tb";
import {useTranslation} from "react-i18next";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {haptic} from "@/helpers/haptic.ts";

interface WishCellProps {
  wish: Wish;
  onClick: () => void;
  isReorderMode?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

export const WishCell = ({
  wish,
  onClick,
  isReorderMode = false,
  canMoveUp = false,
  canMoveDown = false,
  onMoveUp,
  onMoveDown
}: WishCellProps) => {
  const { t } = useTranslation();

  let authProfileFromContext: AuthProfile | undefined = undefined;
  try {
    const { authProfile } = useAuthProfile();
    authProfileFromContext = authProfile
  } catch (e) { /* empty */ }

  let middleSizeImage = wish.image?.sizes[0];
  if (wish.image && wish.image?.sizes.length > 1) {
    middleSizeImage = wish.image?.sizes[1];
  }

  const bookedBadge = wish.bookedBy === "currentUser" &&
    <Badge
      className={"min-w-fit text-green-400"}
      type={"number"} mode={"gray"}>
      {t('wish.bookedByYou')} ✅️
    </Badge>
    || wish.bookedBy === "otherUser" && (!authProfileFromContext || authProfileFromContext?.user.showBookings === "anonymous" || authProfileFromContext?.user.showBookings === "public") &&
    <Badge
      className={"min-w-fit text-gray-400"}
      type={"number"} mode={"gray"}>
      {t('wish.booked')} 🔒
    </Badge>
    || undefined;

  // Render reorder controls if in reorder mode
  const reorderControls = isReorderMode && (
    <div className="flex flex-col gap-1">
      <IconButton
        size="s"
        mode="plain"
        onClick={(e) => {
          e.stopPropagation();
          haptic.selection();
          onMoveUp?.();
        }}
        disabled={!canMoveUp}
        style={{ opacity: canMoveUp ? 1 : 0.3 }}
      >
        <TbChevronUp size={20} />
      </IconButton>
      <IconButton
        size="s"
        mode="plain"
        onClick={(e) => {
          e.stopPropagation();
          haptic.selection();
          onMoveDown?.();
        }}
        disabled={!canMoveDown}
        style={{ opacity: canMoveDown ? 1 : 0.3 }}
      >
        <TbChevronDown size={20} />
      </IconButton>
    </div>
  );

  return (<>
    <Cell onClick={isReorderMode ? undefined : onClick}
          before={<Image className={"shiny-image"}
                         src={middleSizeImage?.link}
                         style={{
                           background: wish.randomGradient,
                         }}
                         fallbackIcon={<TbGift color={"#fff"} size={30}/>}
                         // @ts-expect-error size may have this value
                         size={52}/>}
          className={`gap-3 min-h-20 ${isReorderMode ? 'opacity-75' : ''}`}
          subtitle={wish.description}
          description={<Caption weight="2">{wish.priceText}</Caption>}
          titleBadge={bookedBadge}
          after={isReorderMode ? reorderControls : <Navigation/>}
    >
      {wish.title}
    </Cell>
  </>);
}
